import React, { useState, useRef, useEffect } from 'react';
import './HelperIcon.css';

/**
 * HelperIcon Component
 *
 * A [?] icon that displays explanatory tooltips about the LRB neuro-informed approach.
 * Supports hover (desktop) and click/tap (mobile) interactions.
 *
 * Usage:
 * <HelperIcon
 *   title="What is the LRB Approach?"
 *   content="Our AI uses brain-friendly teaching strategies..."
 * />
 */
const HelperIcon = ({
  title,
  content,
  icon = '?',
  position = 'auto', // 'auto', 'top', 'bottom', 'left', 'right'
  size = 'medium',   // 'small', 'medium', 'large'
  className = ''
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState(position);
  const containerRef = useRef(null);
  const tooltipRef = useRef(null);

  // Handle click for mobile support
  const handleClick = (e) => {
    e.stopPropagation();
    setIsVisible(!isVisible);
  };

  // Handle hover for desktop
  const handleMouseEnter = () => {
    // Only enable hover on non-touch devices
    if (!('ontouchstart' in window)) {
      setIsVisible(true);
    }
  };

  const handleMouseLeave = () => {
    if (!('ontouchstart' in window)) {
      setIsVisible(false);
    }
  };

  // Close tooltip when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsVisible(false);
      }
    };

    if (isVisible) {
      document.addEventListener('click', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isVisible]);

  // Auto-position tooltip to stay within viewport
  useEffect(() => {
    if (isVisible && position === 'auto' && tooltipRef.current && containerRef.current) {
      const tooltip = tooltipRef.current;
      const container = containerRef.current;
      const rect = container.getBoundingClientRect();
      const tooltipRect = tooltip.getBoundingClientRect();

      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      let newPosition = 'bottom';

      // Check if tooltip would go off right edge
      if (rect.left + tooltipRect.width > viewportWidth - 20) {
        newPosition = 'left';
      }
      // Check if tooltip would go off bottom
      else if (rect.bottom + tooltipRect.height > viewportHeight - 20) {
        newPosition = 'top';
      }

      setTooltipPosition(newPosition);
    }
  }, [isVisible, position]);

  const sizeClass = `helper-icon--${size}`;
  const positionClass = `helper-tooltip--${tooltipPosition === 'auto' ? 'bottom' : tooltipPosition}`;

  return (
    <div
      ref={containerRef}
      className={`helper-icon-container ${className}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <button
        className={`helper-icon ${sizeClass}`}
        onClick={handleClick}
        aria-label={`Help: ${title}`}
        aria-expanded={isVisible}
        type="button"
      >
        {icon}
      </button>

      {isVisible && (
        <div
          ref={tooltipRef}
          className={`helper-tooltip ${positionClass}`}
          role="tooltip"
        >
          {title && <h4 className="helper-tooltip__title">{title}</h4>}
          <div className="helper-tooltip__content">
            {typeof content === 'string' ? (
              <p>{content}</p>
            ) : (
              content
            )}
          </div>
          <button
            className="helper-tooltip__close"
            onClick={() => setIsVisible(false)}
            aria-label="Close tooltip"
            type="button"
          >
            &times;
          </button>
        </div>
      )}
    </div>
  );
};

export default HelperIcon;
